import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import { Download, PictureAsPdf, CheckCircle, Info } from '@mui/icons-material';
import { invoiceService } from '../services/invoiceService';

interface InvoicePDFDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number;
  invoiceNumber: string;
}

const InvoicePDFDialog: React.FC<InvoicePDFDialogProps> = ({
  open,
  onClose,
  invoiceId,
  invoiceNumber
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleGeneratePDF = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await invoiceService.generatePDF(invoiceId);
      
      // Decode base64 PDF data
      const pdfData = atob((response as any).pdf_data);
      const pdfArray = new Uint8Array(pdfData.length);
      for (let i = 0; i < pdfData.length; i++) {
        pdfArray[i] = pdfData.charCodeAt(i);
      }
      
      // Create blob and download
      const blob = new Blob([pdfArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = (response as any).filename || `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PictureAsPdf color="primary" />
          <Typography variant="h6">Generate Invoice PDF</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box mb={3}>
          <Alert severity="info" icon={<Info />}>
            <Typography variant="body2">
              Generate a professional PDF invoice with enhanced design and features.
            </Typography>
          </Alert>
        </Box>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Enhanced PDF Features
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box flex="1" minWidth="250px">
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Professional color scheme</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Enhanced typography & spacing</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Status badges with colors</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Alternating row colors</Typography>
              </Box>
            </Box>
            <Box flex="1" minWidth="250px">
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">QR code for payments</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Company branding header</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Professional footer</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">Payment information section</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8fafc' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Invoice:</strong> #{invoiceNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The PDF will include all invoice details with professional formatting and enhanced visual design.
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            PDF generated and downloaded successfully!
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleGeneratePDF}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Download />}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Generate & Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePDFDialog; 